import {computed, observable, reaction, toJS} from 'mobx'
import {
  each,
  find,
  isArray,
  keys,
  mergeWith,
  omit,
  omitBy,
  pick,
  pickBy,
  reduce,
  startsWith,
  union,
} from 'lodash'
import {
  CustomerInfo,
  EpikQueryParam,
  EpmsPaymentConfig,
  EpmsPaymentInfo,
  isCreditCardPaymentMethod,
  normalizeValues,
  PaymentConfig,
  PaymentCurrency,
  PaymentMethod,
  PaymentType,
  RecurringInterval,
  SubscriptionInfo,
  TransactionInfo,
} from '@raisenow/epik'
import {IFormBase} from './IFormBase'
import {
  ValidationConstraint,
  ValidationConstraints,
  ValidationErrors,
} from '../Validator'
import {PaymentFormBase, PaymentFormBaseArgs} from './PaymentFormBase'
import {CookieStore} from '../CookieStore'
import {
  markFieldAsNotDirty,
  subunitsToUnits,
  translateEpikErrorMessages,
  translateLocalErrorMessages,
  unitsToSubunits,
} from '../helpers'
import {WidgetFlag} from '../WidgetFlag'
import {WidgetEventName} from '../WidgetEventer'
import {PaymentSlipDeliveryType} from '../PaymentSlipDeliveryType'
import {Block} from '../Block'

///////////////////////////////////////////////////////////////////////////////

export type PaymentFormData = {
  purpose: string
  payment_type: PaymentType
  recurring_interval: RecurringInterval
  currency: PaymentCurrency
  amount: number

  amount_position: number
  custom_amount: string
  payment_method: PaymentMethod
  last_selected_payment_method: PaymentMethod

  create_payment_source?: boolean
  stored_customer_token?: string

  stored_customer_salutation: string
  stored_customer_firstname: string
  stored_customer_lastname: string
  stored_customer_email: string
  stored_customer_birthdate: string
  stored_customer_email_permission: boolean
  stored_customer_message: string
  stored_customer_donation_receipt: boolean
  stored_customer_street: string
  stored_customer_street_number: string
  stored_customer_street2: string
  stored_customer_pobox: string
  stored_customer_zip_code: string
  stored_customer_city: string
  stored_customer_country: string
  stored_customer_state: string

  stored_campaign_id: string
  stored_campaign_subid: string
  stored_payment_slip_delivery_type: string
  stored_es_ordered: boolean
  stored_ezs_ordered: boolean
  stored_cover_transaction_fee: boolean

  cardno: string
  cvv: string
  expm: string
  expy: string

  iban: string
  bank_name: string
  bank_zip_code: string
  bank_city: string
  bankiban: string
  bankbic: string
  payer_line1: string
  payer_line2: string
  payer_line3: string
  payer_line4: string
  refno: string
  msisdn: string
  recaptcha?: string

  epms_subscription_uuid?: string

  stored_rnw_cover_fee_fixed?: number
  stored_rnw_cover_fee_percentage?: number
  stored_rnw_cover_fee_amount?: number
  stored_rnw_analytics_suggested_amounts?: number
  stored_rnw_analytics_preselected_amount?: number

  [key: string]: any
} & EpmsPaymentConfig

export type PaymentFormArgs = PaymentFormBaseArgs & {
  store: CookieStore
}

///////////////////////////////////////////////////////////////////////////////

export class PaymentForm
  extends PaymentFormBase
  implements IFormBase<PaymentFormData>
{
  @observable data: PaymentFormData
  @observable dirtyFields: string[]
  @observable validationErrors: ValidationErrors
  store: CookieStore

  async init(args: PaymentFormArgs) {
    let {api, store} = args

    await super.init({api})
    this.store = store
    this.reset()
  }

  reset() {
    this.data = createPaymentFormData()
    this.dirtyFields = []
    this.validationErrors = null
    this.resetPayment()
  }

  // this method can be run only after compiler helpers registration
  initDefaults() {
    let {computedConfig} = this.api

    if (computedConfig.paymentFormPrefill) {
      let data = toJS(this.data)
      let prefillData = normalizeValues(toJS(computedConfig.paymentFormPrefill))

      each(prefillData, (v, k) => {
        data[k] = v
      })

      // this needed to reinitialize observables
      // that present in paymentFormPrefill but not initially present in this.data
      this.data = data
    }

    this.data.stored_payment_slip_delivery_type =
      this.getDefaultPaymentSlipDeliveryType()

    reaction(
      () =>
        this.api.resolveConfigWithSingleResult<boolean>(
          'showStoredCustomerEmailPermission',
        ),
      (showStoredCustomerEmailPermission) => {
        if (showStoredCustomerEmailPermission) {
          this.data.stored_customer_email_permission ??= false
        } else {
          this.data.stored_customer_email_permission = null
        }
      },
      {
        fireImmediately: true,
      },
    )

    reaction(
      () => {
        return this.isPaymentCoverFeeEnabled()
      },
      (showPaymentCoverFee) => {
        if (showPaymentCoverFee) {
          this.data.stored_cover_transaction_fee ??= false
        } else {
          this.data.stored_cover_transaction_fee = null
        }
      },
      {
        fireImmediately: true,
      },
    )

    reaction(
      () => {
        return JSON.stringify({
          currentCountry: this.data.stored_customer_country,
          showStoredCustomerState: this.showStoredCustomerState(),
        })
      },
      (jsonData) => {
        const data = JSON.parse(jsonData)
        const {showStoredCustomerState} = data
        const states = this.getStates()
        const currentState = this.data.stored_customer_state

        if (showStoredCustomerState) {
          if (!states.includes(currentState)) {
            this.data.stored_customer_state = null
          }
        } else {
          this.data.stored_customer_state = null
        }

        markFieldAsNotDirty(this.dirtyFields, 'stored_customer_state')
      },
      {
        fireImmediately: true,
      },
    )

    reaction(
      () => JSON.stringify(this.getPossibleAmounts()),
      () => {
        this.clearCustomAmount()
        this.clearAmount()
        this.adjustState()
      },
      {
        fireImmediately: false,
      },
    )

    reaction(
      () =>
        [
          PaymentMethod.PAYMENT_SLIP,
          PaymentMethod.PAYMENT_SLIP_WITH_REFERENCE_NUMBER,
        ].includes(this.getPaymentMethod()!),
      (isTruthy) => {
        if (isTruthy && this.isPaymentSlipForbidden()) {
          this.autocorrectPaymentMethod(true)
        }
      },
      {fireImmediately: true},
    )

    this.autocorrectPurpose(false)
    this.autocorrectPaymentType(false)
    this.autocorrectRecurringInterval(false)
    this.autocorrectCurrency(false)
    this.autocorrectAmount()
    this.autocorrectPaymentMethod(false)
  }

  @computed get validationConstraints(): ValidationConstraints {
    let {computedConfig} = this.api

    return computedConfig.paymentValidations
  }

  @computed get requiredFields(): string[] {
    let constraints = toJS(this.validationConstraints)

    return keys(
      pickBy<ValidationConstraint>(constraints, (v, k) => {
        let isRequired = !!v.required || !!v.accepted
        let hasFalsyCondition =
          !!v.if && !this.api.compiler.checkCondition({if: v.if})

        return isRequired && !hasFalsyCondition
      }),
    )
  }

  updateData(
    data:
      | TransactionInfo
      | SubscriptionInfo
      | CustomerInfo
      | {[key: string]: any},
  ) {
    if (data['epp_transaction_id'] || data['subscription_token']) {
      if (data['stored_rnw_purpose_id']) {
        this.setPurpose(data['stored_rnw_purpose_id'], false)
      }

      if (
        data['stored_rnw_recurring_interval_name'] &&
        data['subscription_token']
      ) {
        this.setPaymentType(PaymentType.RECURRING, false)
        this.setRecurringInterval(data['stored_rnw_recurring_interval_name'])
      }

      if (data['currency']) {
        this.setCurrency(data['currency'], false)
      }

      if (data['amount']) {
        let totalAmount = parseFloat(data['amount']) || 0
        let feeAmount = parseFloat(data['stored_rnw_cover_fee_amount']) || 0

        if (feeAmount) {
          feeAmount = subunitsToUnits(
            feeAmount,
            this.getCurrency(),
            this.api.computedConfig,
          )
        }

        let amount = totalAmount - feeAmount

        amount && this.setAmount(amount, false)
      }

      if (data['payment_method']) {
        let paymentMethod = isCreditCardPaymentMethod(data['payment_method'])
          ? PaymentMethod.CREDIT_CARD
          : data['payment_method']

        this.setPaymentMethod(paymentMethod, false)
      }
    }

    let fields = [
      'stored_customer_salutation',
      'stored_customer_firstname',
      'stored_customer_lastname',
      'stored_customer_email',
      'stored_customer_birthdate',
      'stored_customer_email_permission',
      'stored_customer_message',
      'stored_customer_donation_receipt',
      'stored_customer_street',
      'stored_customer_street_number',
      'stored_customer_street2',
      'stored_customer_pobox',
      'stored_customer_zip_code',
      'stored_customer_city',
      'stored_customer_country',
      'stored_customer_state',
      'bank_name',
      'bank_zip_code',
      'bank_city',
      'msisdn',
      'create_payment_source',
      'stored_customer_token',
    ]

    each(fields, (field) => {
      if (data[field] !== undefined) {
        this.data[field] = data[field]
      }
    })

    this.adjustState()
  }

  updateDataFromQueryParams(params) {
    params = reduce(
      params,
      (result, v, k) => {
        let fieldName = k.replace('rnw-', '')

        result[fieldName] = v

        return result
      },
      {},
    )

    params = normalizeValues(params)

    let specialFields = [
      'purpose',
      'currency',
      'payment_type',
      'recurring_interval',
      'payment_method',
      'amount',
    ]
    let boolFields = [
      'stored_customer_email_permission',
      'stored_customer_donation_receipt',
      'create_payment_source',
    ]
    let restrictedFields = [
      'cardno',
      'expm',
      'expy',
      'cvv',
      'bankiban',
      'bankbic',
      'iban',
      'payer_line1',
      'payer_line2',
      'payer_line3',
      'payer_line4',
    ]
    let data = pick(params, specialFields)

    if (data['purpose']) {
      this.setPurpose(data['purpose'], false)
    }

    if (data['payment_type']) {
      this.setPaymentType(data['payment_type'], false)
    }

    if (data['recurring_interval']) {
      this.setRecurringInterval(data['recurring_interval'], false)
    }

    if (data['currency']) {
      this.setCurrency(data['currency'], false)
    }

    if (data['amount']) {
      const amount = parseFloat(data['amount']) || null
      amount && this.setAmount(amount, false)
    }

    if (data['payment_method']) {
      this.setPaymentMethod(data['payment_method'], false)
    }

    each(boolFields, (field) => {
      if (params[field] === true) {
        this.data[field] = true
      }
    })

    data = omit(params, specialFields, boolFields, restrictedFields)

    each(data, (v, k) => {
      this.data[k] = v
    })

    this.adjustState()
  }

  updateDataFromEpmsPayment(data: EpmsPaymentInfo) {
    const {computedConfig} = this.api

    // todo: refactor - reuse mappings from Epik

    const SUPPORTER_FIELDS = {
      uuid: 'stored_customer_uuid',
      salutation: 'stored_customer_salutation',
      first_name: 'stored_customer_firstname',
      last_name: 'stored_customer_lastname',
      email: 'stored_customer_email',
      email_permission: 'stored_customer_email_permission',
      street: 'stored_customer_street',
      house_number: 'stored_customer_street_number',
      address_addendum: 'stored_customer_street2',
      postal_code: 'stored_customer_zip_code',
      city: 'stored_customer_city',
      country: 'stored_customer_country',
      region_level_1: 'stored_customer_state',
    }

    const SUPPORTER_METADATA_FIELDS = {
      post_office_box: 'stored_customer_pobox',
    }

    each(SUPPORTER_FIELDS, (v, k) => {
      const value = data.supporter_snapshot?.[k]

      if (value) {
        this.data[v] = value
      }
    })

    each(SUPPORTER_METADATA_FIELDS, (v, k) => {
      const value = data.supporter_snapshot?.metadata?.[k]

      if (value) {
        this.data[v] = value
      }
    })

    each(data.supporter_snapshot?.metadata, (v, k) => {
      if (['birth_year', 'birth_month', 'birth_day'].indexOf(k) === -1) {
        this.data[`stored_customer_${k}`] = v
      }
    })

    const birth_year = data.supporter_snapshot?.metadata?.birth_year
    const birth_month = data.supporter_snapshot?.metadata?.birth_month
    const birth_day = data.supporter_snapshot?.metadata?.birth_day

    if (birth_year && birth_month && birth_day) {
      this.data['stored_customer_birthdate'] = [
        birth_year,
        birth_month,
        birth_day,
      ].join('-')
    }

    if (data.custom_parameters.rnw_purpose_id) {
      this.setPurpose(data.custom_parameters.rnw_purpose_id, false)
    }

    if (data.created_subscription_uuid) {
      this.setPaymentType(PaymentType.RECURRING, false)
    } else {
      this.setPaymentType(PaymentType.ONE_TIME, false)
    }

    if (data.custom_parameters.rnw_recurring_interval_name) {
      this.setRecurringInterval(
        data.custom_parameters.rnw_recurring_interval_name,
        false,
      )
    }

    const currency = (
      data.currency_identifier || ''
    ).toLowerCase() as PaymentCurrency
    const totalAmount = subunitsToUnits(data.amount, currency, computedConfig)

    if (currency) {
      this.setCurrency(currency, false)
    }

    if (totalAmount) {
      let feeAmount =
        parseFloat(
          find(data.metadata, (v) => {
            return (
              v.type === 'raisenow' &&
              v.group === 'cover_fee' &&
              v.name === 'amount'
            )
          })?.value,
        ) || 0

      if (feeAmount) {
        feeAmount = subunitsToUnits(
          feeAmount,
          this.getCurrency(),
          this.api.computedConfig,
        )
      }

      const amount = totalAmount - feeAmount

      this.setAmount(amount, false)
    }

    // todo: populate other custom_parameters fields

    this.adjustState()
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // PAYMENT
  //
  /////////////////////////////////////////////////////////////////////////////

  getPaymentConfig(): PaymentConfig {
    let {computedConfig} = this.api
    computedConfig = toJS(computedConfig)
    let formData = toJS(this.data)
    let customData = pickBy(formData, (v, k) => startsWith(k, 'stored_'))
    let isRecurring = this.getPaymentType() === PaymentType.RECURRING
    let immediateExecution =
      this.api.resolveConfigWithSingleResult<boolean>('immediateExecution')

    let isEs = this.getPaymentMethod() === PaymentMethod.PAYMENT_SLIP
    let isEzs =
      this.getPaymentMethod() ===
      PaymentMethod.PAYMENT_SLIP_WITH_REFERENCE_NUMBER
    let isDd = this.getPaymentMethod() === PaymentMethod.DIRECT_DEBIT

    let isEsEzsOrdered = null

    if (
      formData.stored_payment_slip_delivery_type ===
      PaymentSlipDeliveryType.DOWNLOAD
    ) {
      isEsEzsOrdered = false
    }

    if (
      formData.stored_payment_slip_delivery_type ===
      PaymentSlipDeliveryType.MAIL
    ) {
      isEsEzsOrdered = true
    }

    if (formData['hmac']) {
      customData['hmac'] = formData['hmac']
    }

    if (isDd) {
      let ddFormTemplate =
        this.api.resolveConfigWithSingleResult('ddFormTemplate')

      if (ddFormTemplate) {
        customData['form_template'] = ddFormTemplate
      }
    }

    if (
      this.isPaymentCoverFeeEnabled &&
      formData.stored_cover_transaction_fee
    ) {
      customData.stored_rnw_cover_fee_fixed = unitsToSubunits(
        this.getFeeFixed(),
        this.getCurrency(),
        computedConfig,
      )
      customData.stored_rnw_cover_fee_percentage = this.getFeePercentage()
      customData.stored_rnw_cover_fee_amount = unitsToSubunits(
        this.getFeeAmount(),
        this.getCurrency(),
        computedConfig,
      )
    }

    const suggestedAmounts = this.getPossibleAmounts().map((amount) => {
      return unitsToSubunits(amount, this.getCurrency(), computedConfig)
    })
    customData.stored_rnw_analytics_suggested_amounts =
      JSON.stringify(suggestedAmounts)

    let recaptchaData = formData.recaptcha
      ? {recaptcha: formData.recaptcha}
      : {}

    if (!formData['stored_rnw_product_name']) {
      formData['stored_rnw_product_name'] = process.env.STORED_RNW_PRODUCT_NAME
    }

    if (!formData['stored_rnw_product_version']) {
      formData['stored_rnw_product_version'] =
        process.env.STORED_RNW_PRODUCT_VERSION
    }

    if (!formData['stored_rnw_widget_uuid']) {
      formData['stored_rnw_widget_uuid'] = process.env.STORED_RNW_WIDGET_UUID
    }

    if (!formData['stored_customer_token']) {
      formData['stored_customer_token'] =
        this.api.currentLocation.params[EpikQueryParam.CUSTOMER_TOKEN] ?? null
    }

    let storedRnwPurposeText = formData.stored_rnw_purpose_text

    if (!storedRnwPurposeText) {
      storedRnwPurposeText = this.api.trans(`purposes.${this.getPurpose()}`, {
        defaultValue: null,
      })
    }

    let storedRnwRecurringIntervalText =
      formData.stored_rnw_recurring_interval_text

    if (!storedRnwRecurringIntervalText && isRecurring) {
      storedRnwRecurringIntervalText = this.api.trans(
        `recurring_intervals.${this.getRecurringInterval()}`,
        {
          defaultValue: null,
        },
      )
    }

    let esMessage = formData.es_message

    if (!esMessage) {
      esMessage = this.api.trans(`purposes.${formData.purpose}`, {
        defaultValue: null,
      })
    }

    let data = {
      ...customData,
      ...recaptchaData,

      return_parameters: computedConfig.returnParameters,
      test_mode: computedConfig.testMode,
      language: computedConfig.language,
      further_rnw_interaction: false,
      flow: computedConfig.flow,

      stored_rnw_purpose_id: this.getPurpose(),
      payment_method: this.getPaymentMethod(),
      payment_provider: computedConfig.paymentProvider,
      recurring: isRecurring,
      recurring_interval: isRecurring ? this.getRecurringInterval() : null, // cron expression is set by epik
      immediate_execution: isRecurring ? immediateExecution : null,

      stored_rnw_recurring_interval_name: isRecurring
        ? this.getRecurringInterval()
        : null,
      currency: this.getCurrency(),
      amount: unitsToSubunits(
        this.getTotalAmount(),
        this.getCurrency(),
        computedConfig,
      ),
      create_payment_source: formData.create_payment_source,
      stored_customer_token: formData.stored_customer_token,

      stored_customer_salutation: formData.stored_customer_salutation,
      stored_customer_firstname: formData.stored_customer_firstname,
      stored_customer_lastname: formData.stored_customer_lastname,
      stored_customer_email: formData.stored_customer_email,
      stored_customer_birthdate: formData.stored_customer_birthdate,
      stored_customer_email_permission:
        formData.stored_customer_email_permission,
      stored_customer_message: formData.stored_customer_message,
      stored_customer_donation_receipt:
        formData.stored_customer_donation_receipt,
      stored_customer_street: formData.stored_customer_street,
      stored_customer_street_number: formData.stored_customer_street_number,
      stored_customer_street2: formData.stored_customer_street2,
      stored_customer_pobox: formData.stored_customer_pobox,
      stored_customer_zip_code: formData.stored_customer_zip_code,
      stored_customer_city: formData.stored_customer_city,
      stored_customer_country: formData.stored_customer_country,
      stored_customer_state: formData.stored_customer_state,

      stored_campaign_id: formData.stored_campaign_id,
      stored_campaign_subid: formData.stored_campaign_subid,

      stored_rnw_purpose_text: storedRnwPurposeText,
      stored_rnw_recurring_interval_text: storedRnwRecurringIntervalText,

      es_message: esMessage,
      stored_es_ordered: isEs ? isEsEzsOrdered : null,
      stored_ezs_ordered: isEzs ? isEsEzsOrdered : null,
      stored_cover_transaction_fee: formData.stored_cover_transaction_fee,

      cardno: formData.cardno,
      cvv: formData.cvv,
      expm: formData.expm,
      expy: formData.expy,

      iban: formData.iban,
      bank_name: formData.bank_name,
      bank_zip_code: formData.bank_zip_code,
      bank_city: formData.bank_city,
      bankiban: formData.bankiban,
      bankbic: formData.bankbic,
      payer_line1: formData.payer_line1,
      payer_line2: formData.payer_line2,
      payer_line3: formData.payer_line3,
      payer_line4: formData.payer_line4,
      refno: formData.refno,
      msisdn: formData.msisdn,

      stored_rnw_product_name: formData['stored_rnw_product_name'],
      stored_rnw_product_version: formData['stored_rnw_product_version'],
      stored_rnw_widget_uuid: formData['stored_rnw_widget_uuid'],
      stored_rnw_source_url: window.location.href,

      // epms data
      profile: formData.profile,
      return_url: formData.return_url,
      create_supporter: formData.create_supporter,
      supporter: formData.supporter,
      raisenow: formData.raisenow,
      custom_parameters: formData.custom_parameters,
      payment_information: formData.payment_information,
      statement_descriptor: formData.statement_descriptor,

      // needed to update epms subscription
      epms_subscription_uuid: formData.epms_subscription_uuid,
    } as Partial<PaymentConfig>

    data = omitBy(data, (v, k) => {
      return v === null || v === '' || v === undefined || Number.isNaN(v)
    })

    return data as PaymentConfig
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // VALIDATE AND SEND
  //
  /////////////////////////////////////////////////////////////////////////////

  async validate(onlyDirty: boolean = false): Promise<ValidationErrors> {
    let {events, computedConfig, validator, trans} = this.api

    await events[WidgetEventName.BEFORE_PAYMENT_VALIDATE].publish({
      api: this.api,
    })

    let payment = this.getPayment()
    let localErrors = await validator.validate(
      this.data,
      this.validationConstraints,
      this,
    )
    let epikErrors = await payment.validate()

    localErrors = translateLocalErrorMessages(
      localErrors,
      trans.bind(this.api),
      'validation_errors',
    )
    epikErrors = translateEpikErrorMessages(epikErrors, trans.bind(this.api))

    let allErrors = mergeWith({}, localErrors, epikErrors, (d1, d2) => {
      if (isArray(d1)) {
        return d1.concat(d2)
      }
    })

    let errors = allErrors

    if (onlyDirty) {
      errors = pick(errors, toJS(this.dirtyFields))
    }

    // omit hidden field errors
    errors = this.omitHiddenFieldErrors(errors)

    computedConfig.debug &&
      console.log('[tamaro] PaymentForm validation errors', {allErrors, errors})

    errors = keys(errors).length > 0 ? errors : null
    this.validationErrors = errors
    this.dirtyFields = union(toJS(this.dirtyFields), keys(errors))

    if (!onlyDirty) {
      let eventValidateResult = errors
        ? WidgetEventName.PAYMENT_VALIDATE_ERROR
        : WidgetEventName.PAYMENT_VALIDATE_SUCCESS
      await events[eventValidateResult].publish({api: this.api})
    }

    await events[WidgetEventName.AFTER_PAYMENT_VALIDATE].publish({
      api: this.api,
    })

    return toJS(this.validationErrors)
  }

  async send(): Promise<void> {
    let {flags, events} = this.api

    if (this.api.recaptcha.ref.current) {
      this.api.recaptcha.ref.current.execute()
      this.data.recaptcha = await this.api.recaptcha.promise
    }

    await events[WidgetEventName.BEFORE_PAYMENT_SEND].publish({api: this.api})

    flags.add(WidgetFlag.SHOW_SPINNER_OUTER)

    this.persistData()

    try {
      let payment = this.getPayment()
      await payment.send()
    } catch (error) {
      flags.remove(WidgetFlag.SHOW_SPINNER_OUTER)
      flags.add(WidgetFlag.HAS_EPMS_PAYMENT_RESPONSE_ERROR)
    }

    this.clearPersistedData()
  }

  async validateAndSend(): Promise<void> {
    let {events, flags, ui} = this.api

    await events[WidgetEventName.BEFORE_PAYMENT_VALIDATE_AND_SEND].publish({
      api: this.api,
    })

    if (flags.has(WidgetFlag.PREVENT_PAYMENT_VALIDATE_AND_SEND)) {
      return
    }

    let errors = await this.validate(false)

    if (errors) {
      await ui.scrollToTopInvalidField()
      await ui.focusTopInvalidField()

      return
    }

    await this.send()
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // FORM PERSISTANCE
  //
  /////////////////////////////////////////////////////////////////////////////

  persistData() {
    let ignore = ['cardno', 'cvv', 'expm', 'expy']
    let data = omit(toJS(this.data), ignore)

    this.store.set(data)
  }

  restorePersistedData() {
    let data = this.store.get()

    if (data) {
      each(data, (v, k) => {
        if (this.data[k] !== undefined) {
          this.data[k] = v
        }
      })

      this.adjustState()
      this.clearPersistedData()
    }
  }

  clearPersistedData() {
    this.store.clear()
  }
}

///////////////////////////////////////////////////////////////////////////////

export function createPaymentForm(): PaymentForm {
  return new PaymentForm()
}

export function createPaymentFormData(
  data: Partial<PaymentFormData> = {},
): PaymentFormData {
  return {
    purpose: null,
    payment_type: null,
    recurring_interval: null,
    currency: null,
    amount: null,
    amount_position: null,
    custom_amount: null,
    payment_method: null,
    last_selected_payment_method: null,
    create_payment_source: false,
    stored_customer_token: null,

    stored_customer_salutation: null,
    stored_customer_firstname: null,
    stored_customer_lastname: null,
    stored_customer_email: null,
    stored_customer_birthdate: null,
    stored_customer_email_permission: null,
    stored_customer_message: null,
    stored_customer_donation_receipt: false,
    stored_customer_street: null,
    stored_customer_street_number: null,
    stored_customer_street2: null,
    stored_customer_pobox: null,
    stored_customer_zip_code: null,
    stored_customer_city: null,
    stored_customer_country: null,
    stored_customer_state: null,
    stored_campaign_id: null,
    stored_campaign_subid: null,
    stored_payment_slip_delivery_type: null,
    stored_es_ordered: null,
    stored_ezs_ordered: null,
    stored_cover_transaction_fee: null,

    cardno: null,
    cvv: null,
    expm: null,
    expy: null,

    iban: null,
    bank_name: null,
    bank_zip_code: '-',
    bank_city: '-',
    bankiban: null,
    bankbic: null,
    payer_line1: null,
    payer_line2: null,
    payer_line3: null,
    payer_line4: null,
    refno: null,
    msisdn: null,

    // epms data
    profile: null,
    return_url: null,
    create_supporter: false,
    supporter: {},
    raisenow: {},
    custom_parameters: {},
    payment_information: {},

    epms_subscription_uuid: null,

    ...data,
  }
}
